import { useRef, useEffect, useCallback } from "react";

interface DateOfBirthInputProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  error?: boolean;
}

export function DateOfBirthInput({ value, onChange, error }: DateOfBirthInputProps) {
  const mmRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLInputElement>(null);
  const yyyyRef = useRef<HTMLInputElement>(null);

  // Parse YYYY-MM-DD into parts
  const parts = value ? value.split("-") : [];
  const yyyy = parts[0] ?? "";
  const mm = parts[1] ?? "";
  const dd = parts[2] ?? "";

  const buildValue = useCallback(
    (newMM: string, newDD: string, newYYYY: string) => {
      // Only emit a full date string if at least one part has content
      if (!newMM && !newDD && !newYYYY) {
        onChange("");
        return;
      }
      const padded = `${newYYYY.padStart(4, "0")}-${newMM.padStart(2, "0")}-${newDD.padStart(2, "0")}`;
      onChange(padded);
    },
    [onChange],
  );

  // Sync refs on mount when a value already exists (no-op render)
  useEffect(() => {
    if (mmRef.current) mmRef.current.value = mm;
    if (ddRef.current) ddRef.current.value = dd;
    if (yyyyRef.current) yyyyRef.current.value = yyyy;
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMMChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 2);
    e.target.value = v;
    if (v.length === 2) ddRef.current?.focus();
    buildValue(v, ddRef.current?.value ?? dd, yyyyRef.current?.value ?? yyyy);
  };

  const handleDDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 2);
    e.target.value = v;
    if (v.length === 2) yyyyRef.current?.focus();
    buildValue(mmRef.current?.value ?? mm, v, yyyyRef.current?.value ?? yyyy);
  };

  const handleYYYYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 4);
    e.target.value = v;
    buildValue(mmRef.current?.value ?? mm, ddRef.current?.value ?? dd, v);
  };

  const inputClass = `form-input text-center ${error ? "form-input-error" : ""}`;

  return (
    <div className="flex items-center gap-2">
      <input
        ref={mmRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-month"
        enterKeyHint="next"
        maxLength={2}
        placeholder="MM"
        defaultValue={mm}
        onChange={handleMMChange}
        className={`${inputClass} w-16`}
        aria-label="Month"
      />
      <span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
        /
      </span>
      <input
        ref={ddRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-day"
        enterKeyHint="next"
        maxLength={2}
        placeholder="DD"
        defaultValue={dd}
        onChange={handleDDChange}
        className={`${inputClass} w-16`}
        aria-label="Day"
      />
      <span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
        /
      </span>
      <input
        ref={yyyyRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="bday-year"
        enterKeyHint="done"
        maxLength={4}
        placeholder="YYYY"
        defaultValue={yyyy}
        onChange={handleYYYYChange}
        className={`${inputClass} w-20`}
        aria-label="Year"
      />
    </div>
  );
}
